// NavSidebar.tsx — Sidebar navigation.
//
// Renders 9 Roman-numeral sidebar items per the Lore-approved copy table
// (SN-01..SN-09). Active section tracked via activePage prop.
//
// On mobile (< 1100px): horizontal scrollable tab strip.
// On desktop (≥ 1100px): vertical sticky sidebar (240px) — CSS handles layout.
//
// Roman numerals in --font-mark (Cinzel Decorative). Item text in --font-ui.
// Tap target ≥ 44px enforced by .sidebar-item a min-height in admin.css.

import React from 'react';

export type PageId =
  | 'overview'
  | 'news'
  | 'operations'
  | 'business'
  | 'gameplay'
  | 'users'
  | 'analytics'
  | 'controls'
  | 'audit';

interface NavItem {
  id: PageId;
  numeral: string;
  label: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'overview',   numeral: 'I',    label: 'Overview',       href: '#overview' },
  { id: 'news',       numeral: 'II',   label: 'ICP News',       href: '#news' },
  { id: 'operations', numeral: 'III',  label: 'Operations',     href: '#operations' },
  { id: 'business',   numeral: 'IV',   label: 'Business',       href: '#business' },
  { id: 'gameplay',   numeral: 'V',    label: 'Gameplay',       href: '#gameplay' },
  { id: 'users',      numeral: 'VI',   label: 'Users',          href: '#users' },
  { id: 'analytics',  numeral: 'VII',  label: 'Site Analytics', href: '#analytics' },
  { id: 'controls',   numeral: 'VIII', label: 'Controls',       href: '#controls' },
  { id: 'audit',      numeral: 'IX',   label: 'Audit Log',      href: '#audit' },
] as const;

interface NavSidebarProps {
  activePage: PageId;
  onNavigate: (page: PageId) => void;
}

export default function NavSidebar({ activePage, onNavigate }: NavSidebarProps): React.ReactElement {
  return (
    <nav className="sidebar" aria-label="Console sections">
      <ul className="sidebar-list">
        {NAV_ITEMS.map((item) => (
          <li key={item.id} className={`sidebar-item${activePage === item.id ? ' is-active' : ''}`}>
            <a
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(item.id);
                // Smooth scroll to the section anchor as well.
                const el = document.getElementById(item.id);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <span className="sidebar-numeral">{item.numeral}</span>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
